import { MapViewerNode } from '@3ddv/dvm-internal';
import { AvailableSeatsData, SelectedSeatData } from '@3ddv/igsu-models/definitions/seat.model';
import { MapViewerComponent } from '@3ddv/ngx-dvm-internal';
import { ApplicationRef, Injectable, Injector, NgZone, inject } from '@angular/core';
import { BehaviorSubject, take } from 'rxjs';
import { EventsService } from '../../events/data-access/events.service';
import { PanoramicViewerService } from '../../seat-selection/data-access/panoramic-viewer.service';
import { SeatSelectionState } from '../../seat-selection/data-access/seat-selection.state';
import { RunInZone } from '../utils/run-in-zone';
import { CartService } from './cart.service';
import { SeatViewerService } from './dvm/seat-viewer.service';
import { SectionViewerService } from './dvm/section-viewer.service';
import { ComputedAvailability, ExchangeService } from './exchange.service';
import { ModalService } from './modal.service';

@Injectable({
  providedIn: 'root',
})
export class SeatsService {
  protected ngZone = inject(NgZone);
  protected cd = inject(ApplicationRef);
  private _injector = inject(Injector);
  private _cartService = inject(CartService);
  private _eventService = inject(EventsService);
  private _panoramicViewerService = inject(PanoramicViewerService);
  private _seatSelectionState = inject(SeatSelectionState);
  private _modalService = inject(ModalService);

  public readonly _currentSelectedSeat = new BehaviorSubject<SelectedSeatData | null>(null);
  public readonly currentSelectedSeat$ = this._currentSelectedSeat.asObservable();
  public readonly _selectedSeats = new BehaviorSubject<SelectedSeatData[] | null>(null);
  private readonly _availableSeats = new BehaviorSubject<AvailableSeatsData | null>(null);
  public readonly selectedSeats$ = this._selectedSeats.asObservable();
  public readonly availableSeats$ = this._availableSeats.asObservable();
  public seatOnPanoViewer: string | null = null;

  public getCurrentSelectedSeat = () => {
    return this._currentSelectedSeat.getValue();
  };

  public getSelectedSeatsData = () => {
    return this._selectedSeats.getValue();
  };

  /**
   * Sets the available section data.
   * @param availableSectionData Array of SectionModel to set as available.
   */
  public setAvailableSeats(availableSeats: AvailableSeatsData) {
    this._availableSeats.next(availableSeats);
  }

  /**
   * Sets the available section data.
   * @param availableSectionData Array of SectionModel to set as available.
   */
  public getAvailableSeats() {
    return this._availableSeats.getValue();
  }
  /**
   * Adds a selected seat to the selected seats array and updates the BehaviorSubject.
   *
   * @param selectedSeat - The selected seat data to add.
   */
  @RunInZone()
  public setSelectedSeatToArray(selectedSeat: SelectedSeatData) {
    const selectedSeatsValue = this._selectedSeats.getValue() ?? [];
    if (selectedSeatsValue) {
      this._selectedSeats.next([...(selectedSeatsValue as SelectedSeatData[]), selectedSeat]);
    }
  }

  public resetAvailableSeats = () => {
    this._availableSeats.next(null);
  };

  public resetSeatSelectedData = () => {
    this._selectedSeats.next(null);
  };

  /**
   * Unselects all seats and updates the map by calling the unselectAll method of the section viewer service.
   *
   * @param sectionViewerService - The service used to unselect all seats on the map.
   */
  public unselectSeatAndUpdateMap = (sectionViewerService: MapViewerComponent['service'] | null) => {
    sectionViewerService?.unselectAll();
  };

  /**
   * Retrieves seat data for a given node ID.
   *
   * @param nodeID - The node ID to find the corresponding seat data.
   * @returns The seat data that matches the node ID.
   */
  public getSeatFromNodeID = (nodeID: MapViewerNode['id']) => {
    return this._availableSeats.getValue()?.availableSeats.find(seat => +seat.id === +nodeID);
  };

  /**
   * Checks if any seats are currently selected.
   *
   * @returns True if there are selected seats, false otherwise.
   */
  public isSeatSelected = () => {
    return this._selectedSeats.getValue()?.length !== 0;
  };

  /**
   * Removes a seat from the selected seats array.
   *
   * @param seat - The seat data to unselect.
   */
  public unselectSeatFromArray(seat: SelectedSeatData) {
    const filteredSeats = this._selectedSeats
      .getValue()
      ?.filter((seatSelected: SelectedSeatData) => seatSelected.seatData.id !== seat.seatData.id);
    if (filteredSeats) {
      this._selectedSeats.next(filteredSeats);
    }
  }

  /**
   * Toggles the selection of a seat and handles the corresponding actions for selecting the seat.
   *
   * @param event - The event triggered by the seat selection.
   * @param seat - The seat data to toggle selection for.
   */
  public toggleSeatSelection = (event: Event, seat: SelectedSeatData) => {
    event.preventDefault();
    this.handleSeatSelection(seat);
    this._currentSelectedSeat.next(seat);
  };

  /**
   * Handles the logic for selecting a seat, including checking for panoramic views and loading the seat in 3D.
   *
   * @param seat - The seat data to handle.
   */
  private handleSeatSelection(seat: SelectedSeatData) {
    const seatId = `${seat.seatData.section.refKey}-${seat.seatData.id.toString()}`;
    const sectionViewerService = this._injector.get(SectionViewerService);
    const venueId = sectionViewerService.viewer2DRef?.service.getVenueId()!;
    this.seatOnPanoViewer = seat.seatData.section.refKey + '-' + seat.seatData.row + '-' + seat.seatData.seatNumber;

    this._panoramicViewerService.viewer3DRef.service
      .checkView3d({
        venue_id: venueId,
        view_id: seatId,
      })
      .pipe(take(1))
      .subscribe(isViewAvailable => {
        if (isViewAvailable) {
          this._panoramicViewerService.loadMap3D(seatId);
          this._seatSelectionState.setViewer3DState();
          const seatViewerService = this._injector.get(SeatViewerService);
          seatViewerService._isSeatMapVisible.next(false);
        } else {
          const config = {
            type: 'app',
            text: 'This view is unavailable.',
            buttonText: 'Close',
            buttonAction: () => {
              this._modalService.closeModal();
            },
          };

          this._modalService.openModal(config);
        }
      });
  }

  /**
   * Updates the tickets to be displayed based on the selected price scale.
   *
   * @param selectedSeatData The data of the currently selected seats.
   * @returns An array of tickets that match the selection criteria.
   */
  public updateSeatMapTicketsToDisplay(selectedSeatData: SelectedSeatData[]) {
    const exchangeService = this._injector.get(ExchangeService);
    const eventConfig = this._eventService.getEventConfiguration()!;
    const data = exchangeService.getComputedData();

    if (!data?.length) {
      console.warn('Missing required data or selections.');
      return [];
    }

    // Remove duplicates
    const uniqueData = data.reduce(
      (object, value) => {
        if (object.visited.has(value.seatId)) {
          return object;
        } else {
          object.visited.add(value.seatId);
          object.data.push(value);
          return object;
        }
      },
      { data: [] as ComputedAvailability[], visited: new Set() }
    ).data;

    const ticketsInfo: ComputedAvailability[] = [];
    const selectedSeatDataMap = selectedSeatData.map(data => ({
      id: data.seatData.id,
      row: data.seatData.row,
    }));

    for (const ticket of uniqueData.filter(({ seatId }) => selectedSeatDataMap.find(({ id }) => id === seatId))) {
      const row = selectedSeatDataMap.find(({ id }) => id === ticket.seatId)!.row;
      if (eventConfig.shouldPreserveBuyerType) {
        const ticketInfo = { ...ticket, row };
        ticketsInfo.push(...Array.from({ length: ticket.occurrences }, () => ticketInfo));
      } else if (+ticket.buyerTypeId === +eventConfig.buyerType) {
        ticketsInfo.push({ ...ticket, row });
      }
    }

    return ticketsInfo;
  }
  /**
   * Retrieves the data for a specific seat based on the seat ID.
   *
   * @param  seatId - The ID of the seat to retrieve data for.
   * @returns The seat data and price data for the selected seat if both are available.
   */
  public getSeatData = (seatId: number, buyerType: number) => {
    const availableSeatsData = this.getAvailableSeats()!;

    const seatData = availableSeatsData?.availableSeats.find(seat => {
      return seat.id === seatId;
    });

    const priceData = availableSeatsData?.pricePoints.find(priceEle => {
      return (
        priceEle.priceScale.refKey === seatData?.priceScale.refKey && priceEle.buyerType.refKey === buyerType.toString()
      );
    });

    if (seatData && priceData) {
      return { seatData, priceData } as SelectedSeatData;
    }

    return;
  };

  /**
   * Converts an array of selected seat data into raw seat IDs.
   *
   * @param  seatsData - An array of selected seat data.
   * @returns An array of raw seat IDs in the format "sectionRefKey-seatId".
   */
  public getRawSeatIds = (seatsData: SelectedSeatData[]) => {
    const rawSelectedSeats = seatsData.map(seat => {
      return `${seat.seatData?.section?.refKey}-${seat.seatData?.id}`;
    });
    return rawSelectedSeats;
  };

  /**
   * Retrieves the raw seat IDs of the currently selected seats.
   *
   * @returns An array of raw seat IDs for the selected seats, or an empty array if no seats are selected.
   */
  public getSelectedSeats = () => {
    const selectedSeats = this.getSelectedSeatsData();

    if (selectedSeats) {
      const rawIds = this.getRawSeatIds(selectedSeats);
      return rawIds;
    }
    return [];
  };

  /**
   * Updates the total price based on the selected tickets and the current exchange rate.
   * It retrieves the necessary services and computes the new total price, setting it via the setTotalPrice method.
   */
  public updateTotalPrice() {
    const newPrice = this.computeSeatTotalPrice();

    this._cartService.setTotalPrice(newPrice || 0);
  }

  private computeSeatTotalPrice() {
    const exchangeService = this._injector.get(ExchangeService);
    const selectedSeats = this.getSelectedSeatsData();
    const computedData = exchangeService.getComputedData();

    if (selectedSeats == null) {
      return;
    }

    if (selectedSeats?.length === 0) {
      return 0;
    }

    const totalPrice = selectedSeats.reduce((accumulator, current) => {
      const computedSeat = computedData?.find(data => data.seatId === current.seatData.id)!;
      return accumulator + computedSeat.price;
    }, 0);

    return totalPrice;
  }
}
